import React, { createContext, FunctionComponent, useContext, ReactNode } from 'react';
import { connect, useDispatch } from 'react-redux';
import {
  Language,
  MeetingBooking,
  Brand,
  Organization,
  BrandLogo,
  BrandExternalSite,
} from 'app/constants/schemas';
import { Fallback, makeGetEntityTranslation } from '../containers/entities/EntityTranslation';
import { useBookingState } from '../containers/rtc/BookingContext/BookingState';
import { getEntityOfType, getEntityOfTypeOrThrow } from '../models/entities/selectors';
import { AppState } from '../store';
import { EntityStringId, EntityFields, Entity, createEntityStringId } from '../types/entity';
import { useDisplayLanguage } from './LanguageContext';
import { createStructuredSelector } from 'reselect';
import GetEntity from 'app/containers/entities/GetEntity';
import { fetchBrands } from 'app/models/entities/remote';
import { LoadingStage } from 'app/containers/state/LoadingState';
import { getResponseIds } from 'app/remote/response';
import { makeCachedLoader, throwOnError } from 'app/containers/state/makeCachedLoader';
import { AppDispatch } from 'redux';
import { List } from 'immutable';
import { parseUrlLoose, getFullUrl } from 'app/utils/LooseURL';
import { tFunctionType } from 'app/services/localization';
import { LogoComponentType } from 'app/components/svgs/Logo';
import { IBrand } from 'app/constants/schemas/Brand';
import { IBrandDomain, BrandDomain } from 'app/constants/schemas/BrandDomain';
import { ENGLISH } from 'app/constants/enums/Language';
import { ASIA_TOKYO } from 'app/constants/schemas/Timezone';
import { getEntityFields } from 'app/utils/schema';
import { getActiveSiteFeatures, getDefaultDomain } from 'app/models/entities/selectors/brands';
import { Option, chain, map, fold, fromNullable } from 'fp-ts/lib/Option';
import { pipe } from 'fp-ts/lib/pipeable';
import { identity } from 'fp-ts/lib/function';
import { map as arrayMap, rights } from 'fp-ts/lib/Array';
import SvgBellbirdLogo from './svgs/logos/BellbirdLogo';
import SvgHeadsetOwl from './svgs/mascot/HeadsetOwl';
import { ensureBrandReference } from 'app/services/useLoadBrand';
import { fromPredicate } from 'fp-ts/lib/Either';
import { BookingType } from 'app/models/rtc/booking/constants';
import { IOrganization } from 'app/constants/schemas/Organization';
import { ENGOO_GLOBAL_BRAND_ID } from 'app/constants/Brands';
import SvgEngooLogo from './svgs/logos/EngooLogo';
import { resolveDynamicRouteInBrandDomain } from 'app/utils/brand';
import Redirect from './router/Redirect';
import { MeetingBookingRoute } from 'app/constants/Routes';
import SvgEngooSmallLogo from './svgs/logos/EngooSmallLogo';
import { NO_FEATURES_ENABLED, ConfigurationForSiteFeatures } from 'app/constants/schemas/SiteFeatures/siteFeatureTypes';


interface ILink {
  getName: (t: tFunctionType) => string;
  href: string;
}

interface IBrandComputedValues {
  /**
   * Localized brand name string.
   */
  name: string;
  /**
   * Localized brand short name string.
   */
  shortName: string | null;
  /**
   * Localized brand description string.
   */
  description: string;
  /**
   * Localized meeting brand string.
   *
   * @deprecated
   * Should only be used on the meeting landing pgae.
   * Will eventually be removed in favor of making Bellbird into it's own brand.
   */
  meetingBrand: string;
  /**
   * One of a Brand's domains must be default; it is pulled out and placed here for convenience.
   */
  defaultDomain: EntityFields<IBrandDomain>;
  /**
   * Does this brand belong to the (a) first party organization.
   * @deprecated Wherever possible it is better to rely on actual generic
   * abstractions in the brand model than to use this flag to configure a
   * site feature.
   */
  isFirstParty: boolean;

  /**
   * A React component for rendering the configured logo of this brand.
   */
  LogoComponent: LogoComponentType;
  /**
   * A React component for rendering the configured footer logo of this brand. Defaults to the more general logo
   * if one has been defined for the brand and a footer specific one has not.
   */
  FooterLogoComponent: LogoComponentType;
  /**
   * A React component for rendering the configured square logo of this brand.
   */
  SquareLogoComponent?: LogoComponentType;
  /**
   * A collection of links to render in the footer and navigation drawer for this brand.
   */
  brandLinks: List<ILink>;
  /**
   * The dashboard url for this brand (taking branch servers into account).
   */
  dashboardUrl: string | null;
  /**
   * The landing page url for this brand (taking branch servers into account).
   */
  landingPageUrl: string | null;
  /**
   * The RTC guide url for this brand (taking branch servers into account).
   */
  rtcGuideUrl: string | null
  /**
   * The link to an offsite profile url for a user.
   */
  externalUserProfileUrl: string | null;
  /**
   * Meeting support url
   */
  meetingFeatureSupportUrl: string | null;
  /**
   * The set of site features provided by this brand / organization
   */
  siteFeatures: ConfigurationForSiteFeatures;
}
export type BrandContextValue = Omit<EntityFields<IBrand>, keyof IBrandComputedValues> & IBrandComputedValues;

const DEFAULT_LOGO: LogoComponentType = SvgEngooLogo;
const ENGOO_SQUARE_LOGO: LogoComponentType = SvgEngooSmallLogo;
const DEFAULT_SQUARE_LOGO: LogoComponentType = SvgHeadsetOwl;

// This value is only intended for testing as a real value should be set by the
// app entry when the page loads. This value is here because it drastically
// reduces the amount of work needed to get test working.
export const BASE_BRAND_CONTEXT: BrandContextValue = {
  id: ENGOO_GLOBAL_BRAND_ID,
  name: 'Uninitialized Brand',
  shortName: null,
  description: 'Uninitalized Brand: Intended for testing only',
  meetingBrand: 'TestBird',
  defaultDomain: {
    id: createEntityStringId(),
    hostname: 'https://engoo.com',
    pathPrefix: '/app',
    envUri: 'https://localhost:3000/app',
    default: true,
    devSubdomain: false,
    deprecated: false,
  },
  footerLogo: null,
  squareLogo: null,
  logo: null,
  organization: createEntityStringId(),
  dashboardUrl: null,
  landingPageUrl: null,
  rtcGuideUrl: null,
  termsOfServiceUrl: null,
  privacyPolicyUrl: null,
  meetingFeatureSupportUrl: null,
  externalUserProfileUrl: null,
  bookingUrl: null,
  firstPartyDmm: false,
  isFirstParty: true,
  fallbackTimezone: ASIA_TOKYO,
  analyticsIds: List(),
  curricula: List(),
  domains: List(),
  externalSites: List(),
  brandLinks: List(),
  defaultLanguage: ENGLISH,
  canonicalLanguages: List(),
  canonicalLanguageFallback: true,
  LogoComponent: () => <div data-testid="brand-logo" />,
  FooterLogoComponent: () => <div data-testid="brand-logo-footer" />,
  SquareLogoComponent: () => <div data-testid="brand-logo-square" />,
  createdAt: new Date(),
  updatedAt: new Date(),
  siteFeatures: NO_FEATURES_ENABLED,
};

const BrandContext = createContext<BrandContextValue>(BASE_BRAND_CONTEXT);
interface IBaseProviderConnectedProps {
  brand?: Brand;
  /**
   * On meeting bookings, force the bellbird logo. Adding this behavior here removed an otherwise
   * superflous provider. TODO: Configure a separate meeting logo on the Brand?
   */
  bellbirdOverlay?: (Partial<EntityFields<IBrand>> & Partial<IBrandComputedValues>) | undefined;
  displayLanguage: Language;
}

const makeLogoComponent = (image: BrandLogo | null): LogoComponentType => {
  return ({ height, width, ...props }) => {
    const style = { height, width };
    const brand = useBrand();
    const brandName = brand.name;
    return image ? (
      <img src={image.url} alt={brandName} style={style} {...props} />
    ) : null;
  };
};

interface IDomainFragment {
  hostname: string;
  readonly envUri: string;
}
const addServerName = (urlStr: string, domains: List<IDomainFragment>) => {
  const url = parseUrlLoose(urlStr);
  const match = domains.find(d => d.hostname === url.hostname);
  if (match) {
    // This accounts for server names on branch servers, localhost names, and does nothing on production.
    const matchedUrl = parseUrlLoose(match.envUri);
    url.hostname = matchedUrl.hostname;
    url.port = matchedUrl.port;
  }
  return getFullUrl(url);
};

export function maybeAddServerName(urlStr: null, domains: List<IDomainFragment>): null;
export function maybeAddServerName(urlStr: string, domains: List<IDomainFragment>): string;
export function maybeAddServerName(urlStr: string | null, domains: List<IDomainFragment>): string | null;
export function maybeAddServerName(urlStr: string | null, domains: List<IDomainFragment>): string | null {
  return urlStr && addServerName(urlStr, domains);
}

const urlSelector = (state: AppState, brand: Brand, url: string | null): string | null => {
  const domains: List<IDomainFragment> = brand.domains.map(dId => getEntityOfTypeOrThrow(state, BrandDomain, dId));
  const fragments = domains.concat(
    brand.externalSites.map(esId => getEntityOfTypeOrThrow(state, BrandExternalSite, esId)),
  );
  return maybeAddServerName(url, fragments);
};

/**
 * A collection of Bellbird specific values to override with in situations where the bellbird
 * brand is being forced.
 */
const getBellbirdOverlay = (
  state: AppState,
  brandId: EntityStringId,
): Partial<IBrandComputedValues> & Partial<EntityFields<IBrand>> => {
  const brand = getEntityOfTypeOrThrow(state, Brand, brandId);
  const { meetings } = getActiveSiteFeatures(state, brandId);
  const bellbirdTerms = '/app/bellbird/terms/ja.html';
  const links: List<ILink> = pipe(
    [
      {
        getName: (t: tFunctionType) => t('meeting-booking-details.tos-links.terms-of-service'),
        href: bellbirdTerms,
      },
      {
        getName: (t: tFunctionType) => t('meeting-booking-details.tos-links.privacy-policy'),
        href: urlSelector(state, brand, brand.privacyPolicyUrl),
      },
      {
        getName: (t: tFunctionType) => t('meeting-booking-details.tos-links.support'),
        href: urlSelector(state, brand, meetings.configuration?.supportUrl ?? null),
      },
    ],
    arrayMap(fromPredicate((a): a is ILink => a.href !== null, identity)),
    rights,
    (l) => List(l),
  );
  return ({
    meetingBrand: 'Bellbird',
    LogoComponent: SvgBellbirdLogo,
    FooterLogoComponent: SvgBellbirdLogo,
    SquareLogoComponent: SvgHeadsetOwl,
    brandLinks: links,
    termsOfServiceUrl: bellbirdTerms,
  });
};

export const makeBrandContextSelector = () => {
  const getEntityTranslation = makeGetEntityTranslation();
  return createStructuredSelector<
    AppState,
    { brand: Brand; displayLanguage: Language },
    IBrandComputedValues
  >({
    name: (store, { brand, displayLanguage }) =>
      getEntityTranslation(store, {
        type: Brand,
        id: brand.id,
        field: 'name',
        fallback: Fallback.TargetLanguage,
        targetLanguage: brand.defaultLanguage,
        displayLanguage,
        showTranslations: true,
      }).translation,
    shortName: (store, { brand, displayLanguage }) => brand.shortName &&
      getEntityTranslation(store, {
        type: Brand,
        id: brand.id,
        field: 'shortName',
        fallback: Fallback.TargetLanguage,
        targetLanguage: brand.defaultLanguage,
        displayLanguage,
        showTranslations: true,
      }).translation,
    defaultDomain: (store, { brand }) => getDefaultDomain(store, brand.id),
    description: (store, { brand, displayLanguage }) =>
      getEntityTranslation(store, {
        type: Brand,
        id: brand.id,
        field: 'description',
        fallback: Fallback.TargetLanguage,
        targetLanguage: brand.defaultLanguage,
        displayLanguage,
        showTranslations: true,
      }).translation,
    meetingBrand: () => 'Bellbird',
    LogoComponent: (store, { brand }) => {
      const logo = brand.logo ? getEntityOfTypeOrThrow(store, BrandLogo, brand.logo) : null;
      return logo ? makeLogoComponent(logo) : DEFAULT_LOGO;
    },
    FooterLogoComponent: (store, { brand }) => {
      const logo = brand.footerLogo ? getEntityOfTypeOrThrow(store, BrandLogo, brand.footerLogo) :
        brand.logo ? getEntityOfTypeOrThrow(store, BrandLogo, brand.logo) : null;
      return logo ? makeLogoComponent(logo) : DEFAULT_LOGO;
    },
    SquareLogoComponent: (store, { brand }) => {
      const logo = brand.squareLogo && getEntityOfTypeOrThrow(store, BrandLogo, brand.squareLogo);
      /**
       * 1) They don't set any logos -> We just show Engoo branding, both the big and small logo
       * 2) They set a large logo only -> We show their large logo, and the headphone owl
       * 3) They set both logos -> We show their logo, both big and small
       */
      return logo ? makeLogoComponent(logo) : brand.logo ? DEFAULT_SQUARE_LOGO : ENGOO_SQUARE_LOGO;
    },
    brandLinks: (store, { brand }) => {
      const { meetings } = getActiveSiteFeatures(store, brand.id);
      const links: ILink[] = pipe(
        [
          {
            getName: (t: tFunctionType) => t('meeting-booking-details.tos-links.terms-of-service'),
            href: brand.termsOfServiceUrl,
          },
          {
            getName: (t) => t('meeting-booking-details.tos-links.privacy-policy'),
            href: brand.privacyPolicyUrl,
          },
          {
            getName: (t) => t('meeting-booking-details.tos-links.support'),
            href: urlSelector(store, brand, meetings.configuration?.supportUrl ?? null),
          },
        ],
        arrayMap(fromPredicate((a): a is ILink => a.href !== null, identity)),
        rights,
      );
      return List(links);
    },
    siteFeatures: (store, { brand }) => {
      return getActiveSiteFeatures(store, brand.id);
    },
    isFirstParty: (store, { brand }) => {
      const org = getEntityOfTypeOrThrow(store, Organization, brand.organization);
      return org.firstParty;
    },
    dashboardUrl: (store, { brand }) => urlSelector(store, brand, brand.dashboardUrl),
    landingPageUrl: (store, { brand }) => urlSelector(store, brand, brand.landingPageUrl),
    rtcGuideUrl: (store, { brand }) => urlSelector(store, brand, brand.rtcGuideUrl),
    externalUserProfileUrl: (store, { brand }) => {
      const features = getActiveSiteFeatures(store, brand.id);
      const externalUserProfileUrl = features.users.configuration?.externalUserProfileUrl ?? null;
      return urlSelector(store, brand, externalUserProfileUrl);
    },
    meetingFeatureSupportUrl: (store, { brand }) => {
      const features = getActiveSiteFeatures(store, brand.id);
      const meetingFeatureSupportUrl = features.meetings.configuration?.supportUrl ?? null;
      return urlSelector(store, brand, meetingFeatureSupportUrl);
    },
  });
};

const mapStateToProps = () => {
  const getContextValue = makeBrandContextSelector();
  return (store: AppState, {
    brand,
    displayLanguage,
    bellbirdOverlay = {},
  }: Required<IBaseProviderConnectedProps>) => ({
    computedValues: {
      ...getContextValue(store, { brand, displayLanguage }),
      ...bellbirdOverlay,
    },
  });
};

const mapStateToMaybeProps = () => {
  const getContextValue = makeBrandContextSelector();
  return (state: AppState, { brand, displayLanguage }: IBaseProviderConnectedProps) => ({
    computedValues: pipe(
      fromNullable(brand),
      map(someBrand => getContextValue(state, { brand: someBrand, displayLanguage })),
    ),
    brand: fromNullable(brand),
  });
};

const BaseBrandDerivedValues: FunctionComponent<{
  computedValues: IBrandComputedValues,
  brand: Brand,
  children: (brandContext: BrandContextValue) => ReactNode;
}> = function BaseBrandDerivedValues({ computedValues, brand, children }) {
  return (
    <>{children({
      ...getEntityFields(brand),
      ...computedValues,
    })}</>
  );
};

const ConnectedBrandDerivedValues = connect(mapStateToProps)(BaseBrandDerivedValues);
export const GetBrandDerivedValues: FunctionComponent<{
  brandId: EntityStringId;
  children: (brandContext: BrandContextValue) => ReactNode;
}> = ({ brandId, children }) => {
  const displayLanguage = useDisplayLanguage();
  return (
    <GetEntity id={brandId} type={Brand}>{brand =>
      <ConnectedBrandDerivedValues
        brand={brand}
        displayLanguage={displayLanguage}
        children={children}
        bellbirdOverlay={{}}
      />
    }</GetEntity>
  );
};

/**
 * Brand Context Provider which only overrides the value when both given pieces of configuration are defined.
 */
const MaybeOverrideBrandProvider: FunctionComponent<{
  brand: Option<Brand>;
  computedValues: Option<IBrandComputedValues>;
  bellbirdOverlay?: Partial<IBrandComputedValues>;
}> = function MaybeOverrideBrandProvider({
  brand,
  computedValues,
  bellbirdOverlay,
  children,
}) {
  const providedParent = useContext(BrandContext);
  const override: BrandContextValue = pipe(
    computedValues,
    chain(someComputedValues => pipe(
      brand,
      map(someBrand => ({
        ...getEntityFields(someBrand),
        ...someComputedValues,
      })),
    )),
    fold(() => providedParent, identity),
    // The final step is to mix in the bellbird values if it's being forced.
    // This is done separately from the map step to allow bellbird forcing in the absence of a brand override.
    (value) => ({
      ...value,
      ...bellbirdOverlay,
    }),
  );

  return (
    // The structure needs to be maintained to avoid thrashing, so render the parent value through if none was given.
    <BrandContext.Provider value={override}>
      {children}
    </BrandContext.Provider>
  );
};

interface IBaseBrandProviderProps {
  brand?: Brand;
  forceBellbirdLogo?: boolean;
}

/**
 * Simplest form of the BrandProvider which accepts an optionally defined brand and overrides the brand context.
 */
const ConnectedMaybeOverrideBrandProvider = connect(mapStateToMaybeProps)(MaybeOverrideBrandProvider);
export const BaseBrandProvider: FunctionComponent<IBaseBrandProviderProps> = function BaseBrandProvider(props) {
  const displayLanguage = useDisplayLanguage();
  return (
    <ConnectedMaybeOverrideBrandProvider displayLanguage={displayLanguage} {...props} />
  );
};

const BrandOverlayOrRedirect: FunctionComponent<
  IBaseBrandProviderProps & { remoteLocation?: string }
> = ({ remoteLocation, ...rest }) =>
  (remoteLocation) ? <Redirect to={remoteLocation} /> : <BaseBrandProvider {...rest} />;

interface IBookingAwareProps {
  bookingId: EntityStringId | undefined;
  currentBrandId: EntityStringId;
}

/**
 * An BrandProvider that accepts a bookingId, retrieves the associated booking, and passes
 * it's brand to {@link BaseBrandProvider}
 *
 * The context is only overridden if a brand is set on the booking.
 */
const BookingAwareBrandProvider: FunctionComponent<IBookingAwareProps> = connect(
  (store: AppState, { bookingId, currentBrandId }: IBookingAwareProps) => {
    if (!bookingId) return {};

    const booking: MeetingBooking | undefined = getEntityOfType(store, MeetingBooking, bookingId);
    if (!booking) return {};

    const brand = booking.brand ? getEntityOfTypeOrThrow(store, Brand, booking.brand) : undefined;
    const brandOrg = brand ? getEntityOfTypeOrThrow(store, Organization, brand.organization) : undefined;

    if (brand && brand.id !== currentBrandId) {
      const foreignDefaultDomain = getDefaultDomain(store, brand.id);
      return ({
        remoteLocation: resolveDynamicRouteInBrandDomain(foreignDefaultDomain, MeetingBookingRoute, { id: bookingId }),
      });
    }

    return {
      brand,
      // At this point, the booking must have been defined (and be a meeting booking).
      bellbirdOverlay: brand && brandOrg?.firstParty ? getBellbirdOverlay(store, brand.id) : undefined,
    };
  },
)(BrandOverlayOrRedirect);

/**
 * An OrganizationProvider that uses the BookingState to retrieve the information about the current
 * booking
 */
export const BookingStateAwareBrandProvider: FunctionComponent = props => {
  const bookingState = useBookingState();
  const { id } = useBrand();
  return (
    <BookingAwareBrandProvider
      bookingId={bookingState?.bookingId}
      currentBrandId={id}
      {...props}
    />
  );
};

export const BrandConsumer = BrandContext.Consumer;
export const useBrand = () => useContext(BrandContext);

interface IAllBrandsResult {
  stage: LoadingStage;
  brandIds: EntityStringId[];
}

/**
 * `loadLocker` is here to enforce reload of cached results after changes such as deletion happens
 */
export const {
  useLoader: useLoadAllBrands,
} = makeCachedLoader(async (args: { organizationId: EntityStringId, dispatch: AppDispatch, loadLocker?: number }) => {
  const brandIds = List(getResponseIds(
    await args.dispatch(fetchBrands({ organization: args.organizationId })),
  ));
  // Ensure that each of the brands that was loaded has a brand reference in the store
  brandIds.forEach(bId => args.dispatch(ensureBrandReference(bId)));
  return brandIds;
});

export const useAllBrands = (organizationId: EntityStringId): IAllBrandsResult => {
  const dispatch = useDispatch();
  const loadAllBrands = throwOnError(useLoadAllBrands({ args: { organizationId, dispatch } }));

  return ({
    stage: loadAllBrands.stage,
    brandIds: loadAllBrands.stage === LoadingStage.Success ? loadAllBrands.value.toArray() : [],
  });
};

export const GetActiveBrand: FunctionComponent<{
  children: (result: { activeBrand: Entity<IBrand>; organization: Entity<IOrganization> }) => ReactNode;
}> = ({ children }) => {
  const brand = useBrand();
  const bookingState = useBookingState();
  const activeBrandId =
    bookingState?.bookingType === BookingType.Lesson ? bookingState.brand?.id ?? brand.id : brand.id;

  return (
    <GetEntity type={Brand} id={activeBrandId}>
      {activeBrand => (
        <GetEntity type={Organization} id={activeBrand.organization}>
          {organization => children({ activeBrand, organization })}
        </GetEntity>
      )}
    </GetEntity>
  );
};
